import type React from 'react';

import { Text } from '@change/ds-core-ui';

import { BlurredBackdrop } from '../BlurredBackdrop';
import { CreatorBadge } from '../CreatorBadge';
import { TikTokPlayer } from '../TikTokPlayer';

/**
 * Desktop video-first hero (design V8): creator badge and title sit above a full-width
 * black media area. The 9:16 embed is centred in the left portion, with a blurred copy
 * of the video's cover frame filling the letterboxing on either side. The sign card is
 * rendered by the page grid and overlaps the right edge of this media area.
 */
export const VideoFirstHeroDesktop: React.FC<Props> = ({ coverUrl, creatorName, playerUrl, title, titleDataQa }) => {
	return (
		<>
			<div data-qa="video-first-hero" className="col-span-full hidden w-full px-10 md:row-start-1 md:block">
				<div className="mx-auto flex w-full max-w-[1176px] flex-col items-start gap-4 py-6">
					<CreatorBadge name={creatorName} tone="on-light" />
					<Text
						as="h1"
						variant="heading"
						size="x-large"
						fontWeight="bold"
						data-qa={titleDataQa}
						className="text-typography-strong text-wrap"
					>
						{title}
					</Text>
				</div>
			</div>

			{/* 784px tall in the design, capped so the sign card's CTA stays above the fold on short viewports */}
			<div className="relative col-span-full hidden h-[784px] max-h-[calc(100svh-14rem)] w-full overflow-hidden rounded-xl bg-black md:row-start-2 md:block">
				<BlurredBackdrop coverUrl={coverUrl} />

				{/* 376px = the 352px sticky sign card that overlaps this area, plus its 24px gutter */}
				<div className="absolute inset-y-0 left-0 flex w-[calc(100%-376px)] items-center justify-center">
					<div className="aspect-[9/16] h-full">
						{playerUrl && <TikTokPlayer playerUrl={playerUrl} title={title} viewport="desktop" />}
					</div>
				</div>
			</div>
		</>
	);
};

type Props = {
	coverUrl: string | undefined;
	creatorName: string | undefined;
	playerUrl: string | undefined;
	title: string;
	titleDataQa: string | undefined;
};
